import { useMe } from "@/api/services/main/auth";
import { IconClose } from "@/assets/icons";
import { IconChat } from "@/assets/icons/IconChat";
import { ChatBox } from "@/features/chat/components/ChatBox";
import {
  BUTTON_CHAT_SIZE,
  CHAT_BOX_BOTTOM_GAP,
  CHAT_BOX_WIDTH,
  CHAT_ICON_SIZE,
  CLOSE_ICON_SIZE,
} from "@/features/chat/constants/chat";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { useVisible } from "@/hooks/useVisible";
import { background, state } from "@/theme/colors";
import { FCC } from "@/types/common";
import { getQueryFromURL } from "@/utils/url";
import { Box, Button, Stack, styled } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

type TChatProps = {};

export const Chat: FCC<TChatProps> = ({}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = getQueryFromURL();
  const chatPopup = useVisible(!!query && "chat" in query);
  const { currentRoom } = useChat();
  const [unread, setUnread] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 10, y: -10 });
  const dragRef = useRef<HTMLDivElement>(null);
  const startPos = useRef({ x: 0, y: 0 });
  const startMousePos = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);
  const { data: meResult } = useMe();

  useEffect(() => {
    const mergedMessages = currentRoom.isFetching
      ? currentRoom.oldMessages
      : [
          ...currentRoom.oldMessages,
          ...currentRoom.newMessages.filter((newMsg) => !currentRoom.oldMessages.some((oldMsg) => oldMsg.id === newMsg.id)),
        ];

    const latestMessage = mergedMessages?.filter((msg) => msg.position === "left").at(-1);

    if (
      !chatPopup.visible &&
      !!meResult &&
      latestMessage &&
      !latestMessage?.readUserIds.some((id) => id.toString() === meResult.data.id.toString())
    ) {
      setUnread(true);
    } else {
      setUnread(false);
    }
  }, [currentRoom.oldMessages, currentRoom.newMessages, chatPopup.visible, meResult]);

  const toggleChat = () => {
    if (isDragging || hasDragged.current) return;
    chatPopup.toggle();
    const newSearchParams = new URLSearchParams(searchParams);
    if (unread) setUnread(false);
    if (chatPopup.visible) {
      newSearchParams.delete("chat");
    } else {
      newSearchParams.set("chat", "open");
    }
    setSearchParams(newSearchParams);
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      hasDragged.current = false;
      startPos.current = { x: position.x, y: position.y };
      startMousePos.current = { x: e.clientX, y: e.clientY };
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startMousePos.current.x;
      const deltaY = e.clientY - startMousePos.current.y;
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasDragged.current = true;
        if (!chatPopup.visible) {
          if (typeof chatPopup.open === "function") {
            chatPopup.open();
          } else {
            chatPopup.toggle();
          }
        }
      }

      const chatBoxWidth = CHAT_BOX_WIDTH;
      const chatBoxHeight = window.innerHeight * 0.75 - 100;
      const buttonSize = BUTTON_CHAT_SIZE;

      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newRight = startPos.current.x - deltaX;
      let newBottom = startPos.current.y - deltaY;

      const minRight = 0;
      const maxRight = viewportWidth - chatBoxWidth;
      const minBottom = -20;
      const maxBottom = viewportHeight - (chatBoxHeight + buttonSize + CHAT_BOX_BOTTOM_GAP);

      newRight = Math.min(Math.max(newRight, minRight), maxRight);
      newBottom = Math.min(Math.max(newBottom, minBottom), maxBottom);

      setPosition({ x: newRight, y: newBottom });
    },
    [isDragging, chatPopup],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => {
      hasDragged.current = false;
    }, 100);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <ChatWrapper
      ref={dragRef}
      style={{
        right: `${position.x}px`,
        bottom: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      <ChatBox open={chatPopup.visible} />
      <ChatButton open={chatPopup.visible} onClick={toggleChat} onMouseDown={handleMouseDown} isDragging={isDragging}>
        {unread && <UnreadCircle />}
        <Box>
          {chatPopup.visible ? (
            <IconClose width={CLOSE_ICON_SIZE} height={CLOSE_ICON_SIZE} color="white" />
          ) : (
            <IconChat width={CHAT_ICON_SIZE} height={CHAT_ICON_SIZE} />
          )}
        </Box>
      </ChatButton>
    </ChatWrapper>
  );
};

const ChatWrapper = styled(Stack)`
  position: fixed;
  z-index: 1300;
  transition: none;
`;

const UnreadCircle = styled(Box)`
  position: absolute;
  right: 0;
  top: 4px;
  width: 10px;
  max-width: 10px;
  height: 10px;
  max-height: 10px;
  border-radius: 100px;
  background-color: ${state.error_1};
  border: 0.5px solid white;
`;

const ChatButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isDragging",
})<{ open: boolean; isDragging?: boolean }>`
  width: ${BUTTON_CHAT_SIZE}px;
  height: ${BUTTON_CHAT_SIZE}px;
  max-width: ${BUTTON_CHAT_SIZE}px;
  max-height: ${BUTTON_CHAT_SIZE}px;
  min-width: ${BUTTON_CHAT_SIZE}px;
  min-height: ${BUTTON_CHAT_SIZE}px;
  transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0deg)")};
  transition: ${({ isDragging }) => (isDragging ? "none" : "transform 0.3s ease, background-color 0.3s ease")};
  background-color: ${background.main};
  border-radius: 100px;
  border-width: 0px;
  bottom: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16) !important;
  cursor: ${({ isDragging }) => (isDragging ? "grabbing" : "grab")};
  padding: 0px;
  user-select: none;
  &:hover {
    background-color: ${background.main};
  }

  &:active {
    cursor: grabbing;
  }

  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    transform: ${({ open }) => (open ? "rotateZ(45deg)" : "0")};
    pointer-events: none;
    width: 100%;
    height: 100%;
  }
`;
